import type { ComponentProps, FC } from "react"
import { styled } from "styled-system/jsx"

const BaseCheckbox = styled("input", {
  base: {
    appearance: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "[16px]",
    height: "[16px]",
    rounded: true,
    border: true,
    backgroundColor: "surface",
    cursor: "pointer",
    transitionProperty: "[background-color, border-color]",
    transitionDuration: "[150ms]",
    // The tick is two borders of an empty box, rotated into a checkmark.
    _before: {
      content: '""',
      display: "block",
      width: "[4px]",
      height: "[8px]",
      marginTop: "[-2px]",
      borderRightWidth: "[2px]",
      borderBottomWidth: "[2px]",
      borderStyle: "solid",
      borderColor: "text.onDark",
      transform: "rotate(45deg) scale(0)",
      transitionProperty: "[transform]",
      transitionDuration: "[150ms]",
    },
    _checked: {
      backgroundColor: "primary",
      borderColor: "primary",
      _before: {
        transform: "rotate(45deg) scale(1)",
      },
    },
    _disabled: {
      cursor: "not-allowed",
      opacity: "muted",
    },
    _focusVisible: {
      outline: "[2px solid]",
      outlineColor: "primary",
      outlineOffset: "[2px]",
    },
  },
})

const CheckboxRoot = styled("label", {
  base: {
    display: "inline-flex",
    alignItems: "center",
    gap: "xs",
    cursor: "pointer",
    textStyle: "input",
    userSelect: "none",
    "&:has(:disabled)": {
      cursor: "not-allowed",
      opacity: "muted",
      // The box dims itself for the label-less case, but opacity compounds
      // through nesting, so it has to stand down once this label dims the lot.
      "& input:disabled": { opacity: "[1]" },
    },
  },
})

export type CheckboxProps = Omit<
  ComponentProps<typeof BaseCheckbox>,
  "checked" | "defaultChecked" | "onChange" | "type"
> & {
  checked: boolean
  onCheckedChange(checked: boolean): void
}

// `children` is the checkbox's label; omit it for a standalone box that a
// nearby heading already describes.
export const Checkbox: FC<CheckboxProps> = ({
  children,
  onCheckedChange,
  ...props
}) => {
  const checkbox = (
    <BaseCheckbox
      {...props}
      type="checkbox"
      onChange={(event) => {
        onCheckedChange(event.target.checked)
      }}
    />
  )

  return children ? (
    <CheckboxRoot>
      {checkbox}
      {children}
    </CheckboxRoot>
  ) : (
    checkbox
  )
}
